import React from 'react'
import { Translation } from 'react-i18next'
import { LDObserve } from '@launchdarkly/observability'

interface Props {
  children: React.ReactNode
  /** Optional fallback UI. Defaults to a generic error screen. */
  fallback?: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    LDObserve.recordError(error, 'React Error Boundary', {
      componentStack: errorInfo.componentStack ?? '',
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      // Class component: the useTranslation hook is unavailable here, so we use
      // the <Translation> render-prop instead of the withTranslation() HOC to keep
      // the class itself directly importable (and unit-testable).
      return (
        <Translation ns="common">
          {(tc) => (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
              <p className="text-lg font-semibold text-gray-800">{tc('somethingWentWrong')}</p>
              <p className="text-sm text-gray-500 max-w-md">
                {this.state.error?.message ?? tc('unexpectedError')}
              </p>
              <button
                onClick={this.handleReset}
                className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700 transition-colors"
              >
                {tc('tryAgain')}
              </button>
            </div>
          )}
        </Translation>
      )
    }

    return this.props.children
  }
}
